import { Module } from 'core/Module';


interface FormElements {
	name: HTMLInputElement;
	email: HTMLInputElement;
	company: HTMLInputElement;
	message: HTMLTextAreaElement;
	lastname: HTMLInputElement;
}

export class ContactForm extends Module {
	private form: HTMLFormElement;
	private elements: FormElements;
	private submitButton: HTMLInputElement;

	public create(): void {
		this.form = document.querySelector('.contact__form') as HTMLFormElement;

		if (!this.form) {
			return;
		}

		this.elements = {
			name: document.getElementById('contact__form__name') as HTMLInputElement,
			email: document.getElementById('contact__form__email') as HTMLInputElement,
			company: document.getElementById('contact__form__company') as HTMLInputElement,
			message: document.getElementById('contact__form__message') as HTMLTextAreaElement,
			lastname: document.getElementById('contact__form__lastnamea') as HTMLInputElement
		};

		this.submitButton = this.form.querySelector('.contact__form__submit') as HTMLInputElement;

		this.bindEvents();
	}

	private bindEvents(): void {
		this.form.addEventListener('submit', (e) => this.handleSubmit(e));

		// Real-time validation on blur
		if (this.elements.name) {
			this.elements.name.addEventListener('blur', () => this.validateName());
		}
		if (this.elements.email) {
			this.elements.email.addEventListener('blur', () => this.validateEmail());
		}
		if (this.elements.message) {
			this.elements.message.addEventListener('blur', () => this.validateMessage());
		}
	}

	private handleSubmit(e: Event): void {
		e.preventDefault();

		// Clear previous errors
		this.clearErrors();

		// Validate all fields
		const isNameValid = this.validateName();
		const isEmailValid = this.validateEmail();
		const isMessageValid = this.validateMessage();
		const isNotSpam = this.validateHoneypot();

		console.log('isNameValid', isNameValid, isEmailValid, isMessageValid, isMessageValid);

		// If all validations pass
		if (isNameValid && isEmailValid && isMessageValid && isNotSpam) {
			this.submitForm();
		} else {
			// Scroll to first error
			const firstError = this.form.querySelector('.contact__form__item.error');
			if (firstError) {
				firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}

	private validateName(): boolean {
		const name = this.elements.name.value.trim();
		const container = this.elements.name.closest('.contact__form__item');

		if (!name) {
			this.showError(container, 'Name is required');
			return false;
		}

		if (name.length < 2) {
			this.showError(container, 'Name must be at least 2 characters');
			return false;
		}

		this.clearError(container);
		return true;
	}

	private validateEmail(): boolean {
		const email = this.elements.email.value.trim();
		const container = this.elements.email.closest('.contact__form__item');

		if (!email) {
			this.showError(container, 'Email is required');
			return false;
		}

		// Email regex pattern
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailPattern.test(email)) {
			this.showError(container, 'Please enter a valid email address');
			return false;
		}

		this.clearError(container);
		return true;
	}

	private validateMessage(): boolean {
		const message = this.elements.message.value.trim();
		const container = this.elements.message.closest('.contact__form__item');

		if (!message) {
			this.showError(container, 'Message is required');
			return false;
		}

		if (message.length < 10) {
			this.showError(container, 'Message must be at least 10 characters');
			return false;
		}

		this.clearError(container);
		return true;
	}

	private validateHoneypot(): boolean {
		// Honeypot field should be empty (spam bots usually fill all fields)
		if (this.elements.lastname && this.elements.lastname.value.trim() !== '') {
			console.warn('Honeypot field filled - possible spam');
			return false;
		}
		return true;
	}

	private showError(container: Element | null, message: string): void {
		if (!container) return;

		container.classList.add('error');

		// Remove existing error message if any
		const existingError = container.querySelector('.contact__form__error');
		if (existingError) {
			existingError.remove();
		}

		// Create and append error message
		const errorElement = document.createElement('span');
		errorElement.className = 'contact__form__error';
		errorElement.textContent = message;
		container.appendChild(errorElement);
	}

	private clearError(container: Element | null): void {
		if (!container) return;

		container.classList.remove('error');
		const errorElement = container.querySelector('.contact__form__error');
		if (errorElement) {
			errorElement.remove();
		}
	}

	private clearErrors(): void {
		const errorContainers = this.form.querySelectorAll('.contact__form__item.error');
		errorContainers.forEach((container) => {
			this.clearError(container);
		});
	}

	private submitForm(): void {
		// Disable submit button to prevent double submission
		this.submitButton.disabled = true;
		this.submitButton.value = 'Sending...';

		// Gather form data including honeypot field
		const formData = {
			name: this.elements.name.value.trim(),
			email: this.elements.email.value.trim(),
			company: this.elements.company ? this.elements.company.value.trim() : '',
			message: this.elements.message.value.trim(),
			lastname: this.elements.lastname ? this.elements.lastname.value : ''
		};

		// Get CSRF token from meta tag
		const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

		// Send to API
		fetch('/contact/submit/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-TOKEN': csrfToken || '',
				Accept: 'application/json'
			},
			body: JSON.stringify(formData)
		})
			.then((response) => {
				if (!response.ok) {
					// Handle rate limiting (429 Too Many Requests)
					if (response.status === 429) {
						throw new Error('Too many submission attempts. Please wait a minute before trying again.');
					}

					return response.json().then((data) => {
						throw new Error(data.message || 'Server error');
					});
				}
				return response.json();
			})
			.then((data) => {
				if (data.success) {
					this.handleSuccess();
				} else {
					this.handleError(data.message || 'Unknown error');
				}
			})
			.catch((error) => {
				this.handleError(error);
			});
	}

	private handleSuccess(): void {
		// Reset form
		this.form.reset();

		// Re-enable submit button
		this.submitButton.disabled = false;
		this.submitButton.value = 'Send';

		// Show success message
		const successMessage = document.createElement('div');
		successMessage.className = 'contact__form__success';
		successMessage.textContent = 'Thank you! Your message has been sent successfully.';
		this.form.insertBefore(successMessage, this.form.firstChild);

		// Remove success message after 5 seconds
		setTimeout(() => {
			successMessage.remove();
		}, 5000);
	}

	private handleError(error: any): void {
		// Re-enable submit button
		this.submitButton.disabled = false;
		this.submitButton.value = 'Send';

		// Get error message
		const message =
			error instanceof Error
				? error.message
				: 'Sorry, there was an error sending your message. Please try again or email us directly.';

		// Show error message
		const errorMessage = document.createElement('div');
		errorMessage.className = 'contact__form__error-message';
		errorMessage.textContent = message;
		this.form.insertBefore(errorMessage, this.form.firstChild);

		// Remove error message after 5 seconds
		setTimeout(() => {
			errorMessage.remove();
		}, 5000);
	}
}
